/**
 * DemoShellNav — shared navigation for both demo experiences
 * (Ready-to-Work Workspace + Brain Builder).
 *
 * Users must never feel trapped: every demo offers a Back to landing,
 * Switch Demo, Sign In, and Build My Own escape hatch.
 *
 * Patch 34.1.10 · Competition Landing & Dual-Demo Routing.
 */
import { Link } from "react-router-dom";
import { ArrowLeft, LogIn, Repeat, Sparkles } from "lucide-react";

import { trackDemoEvent } from "@/shared/lib/demoAnalytics";

interface Props {
  /** Which demo we're currently in — controls the "Switch" target. */
  current: "workspace" | "brain-builder";
}

export function DemoShellNav({ current }: Props): JSX.Element {
  const switchTo = current === "workspace" ? "/demo/brain-builder" : "/demo/workspace";
  const switchLabel = current === "workspace" ? "See how it works" : "Explore ready-to-work";
  return (
    <div
      data-testid="demo-shell-nav"
      className="sticky top-0 z-30 flex flex-wrap items-center gap-2 border-b border-[color:var(--md-line)] bg-[var(--md-ink-0)]/90 px-4 py-2 backdrop-blur-lg"
    >
      <Link
        to="/welcome"
        data-testid="demo-nav-back"
        className="md-btn md-btn--ghost inline-flex text-xs"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Orkexra
      </Link>
      <Link
        to={switchTo}
        data-testid="demo-nav-switch"
        onClick={() => trackDemoEvent("demo_switched")}
        className="md-btn md-btn--ghost inline-flex text-xs"
      >
        <Repeat className="h-3.5 w-3.5" /> {switchLabel}
      </Link>
      <div className="ml-auto flex items-center gap-2">
        <Link
          to="/login"
          data-testid="demo-nav-signin"
          onClick={() => trackDemoEvent("demo_signin_clicked")}
          className="md-btn md-btn--ghost inline-flex text-xs"
        >
          <LogIn className="h-3.5 w-3.5" /> Sign in
        </Link>
        <Link
          to="/onboarding"
          data-testid="demo-nav-build-mine"
          onClick={() => trackDemoEvent("demo_build_my_own_clicked")}
          className="md-btn md-btn--primary inline-flex whitespace-nowrap text-xs"
        >
          <Sparkles className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Build my own</span>
          <span className="sm:hidden">Build mine</span>
        </Link>
      </div>
    </div>
  );
}
